/**
 * Safe Pattern Evaluator
 *
 * Safely evaluates AI-generated pattern code strings with validation and sandboxing.
 * Replaces the dangerous `new Function()` approach with security measures.
 */

/**
 * Validates that the code string contains only safe patterns
 * @param {string} codeString - The code to validate
 * @returns {boolean} - True if code appears safe
 */
const validateCodeStructure = (codeString) => {
  // Check for dangerous patterns (import is handled separately by stripping it out)
  const dangerousPatterns = [
    /require\s*\(/i,
    /eval\s*\(/i,
    /Function\s*\(/i,
    /constructor/i,
    /process\./i,
    /global\./i,
    /window\./i,
    /document\./i,
    /fetch\s*\(/i,
    /XMLHttpRequest/i,
    /localStorage/i,
    /sessionStorage/i,
    /__proto__/i,
    /prototype\s*=/i
  ];

  for (const pattern of dangerousPatterns) {
    if (pattern.test(codeString)) {
      console.warn('Dangerous pattern detected:', pattern);
      return false;
    }
  }

  return true;
};

/**
 * Safely evaluates a pattern function code string
 * @param {string} codeString - The function code as a string
 * @returns {Function|null} - The evaluated function or null if unsafe
 */
export const safeEvaluatePattern = (codeString) => {
  if (!codeString || typeof codeString !== 'string') {
    throw new Error('Invalid code string provided');
  }

  // Validate code structure
  if (!validateCodeStructure(codeString)) {
    throw new Error('Code contains potentially dangerous patterns');
  }

  // Clean the code - remove markdown code blocks and import statements
  let cleanCode = codeString
    .replace(/```javascript|```js|```/g, '')
    .trim();

  // Remove all lines that contain import statements (more aggressive approach)
  cleanCode = cleanCode
    .split('\n')
    .filter(line => !line.trim().match(/^import\s+/i))
    .join('\n')
    .trim();

  // Ensure it's in the expected function format (matches original behavior)
  if (!cleanCode.startsWith('(x, y, t, density)')) {
    cleanCode = `(x, y, t, density) => { ${cleanCode} }`;
  }

  try {
    // Create function with restricted scope
    // Only Math object is allowed in scope
    const restrictedScope = {
      Math: Math,
      // Prevent access to dangerous globals
      window: undefined,
      document: undefined,
      global: undefined,
      process: undefined,
      require: undefined,
      Function: undefined
      // Note: Cannot use reserved keywords like 'import', 'eval', 'arguments' as parameter names
    };

    // Create function with restricted scope
    const scopeKeys = Object.keys(restrictedScope);
    const scopeValues = Object.values(restrictedScope);

    // Use Function constructor with restricted scope
    const wrappedCode = `
      "use strict";
      return (${cleanCode});
    `;

    // eslint-disable-next-line no-new-func
    const patternFunc = new Function(...scopeKeys, wrappedCode)(...scopeValues);

    // Test the function with safe values
    const testValue = patternFunc(30, 17.5, 0, 1);

    if (typeof testValue !== 'number' || isNaN(testValue) || !isFinite(testValue)) {
      throw new Error('Function returned invalid value (expected finite number)');
    }

    // Return wrapped safe version with timeout protection
    return (x, y, t, density) => {
      try {
        const result = patternFunc(x, y, t, density);

        // Validate result
        if (typeof result !== 'number' || isNaN(result) || !isFinite(result)) {
          console.warn('Pattern function returned invalid value:', result);
          return 0;
        }

        return result;
      } catch (error) {
        console.error('Pattern execution error:', error);
        return 0;
      }
    };

  } catch (error) {
    console.error('Failed to evaluate pattern function:', error);
    throw new Error(`Pattern function error: ${error.message}`);
  }
};

/**
 * Test if a pattern function is valid by running it with test inputs
 * @param {Function} patternFunc - The pattern function to test
 * @returns {boolean} - True if function is valid
 */
export const testPatternFunction = (patternFunc) => {
  if (typeof patternFunc !== 'function') {
    return false;
  }

  try {
    // Test with various inputs
    const testCases = [
      [0, 0, 0, 1],
      [30, 17.5, 0, 1],
      [100, 100, Math.PI, 0.5],
      [-10, -10, Math.PI * 2, 1.5]
    ];

    for (const [x, y, t, density] of testCases) {
      const result = patternFunc(x, y, t, density);
      if (typeof result !== 'number' || isNaN(result) || !isFinite(result)) {
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Pattern function test failed:', error);
    return false;
  }
};
